// ════════════════════════════════════════════════════════════════
// DYNAMIC FORM — Renders fields from AppConfig FieldDefinitions
// ════════════════════════════════════════════════════════════════
// This is a GENERIC form renderer. It reads the field definitions
// from the config and renders the appropriate input types.
// The same component handles wallet, asset, address, and transaction
// forms — and will work for ANY future niche without modification.
// ════════════════════════════════════════════════════════════════

import { useState, type FormEvent } from 'react';
import type { FieldDefinition } from '../config/app.config';
import type { FormData } from '../types';
import { findSeedPhraseInForm, getSeedPhraseWarning } from '../utils/seedPhraseGuard';
import { QRScanner } from './QR';

interface DynamicFormProps {
  fields: FieldDefinition[];
  title: string;
  initialData?: FormData;
  onSubmit: (data: FormData) => Promise<void>;
  onCancel: () => void;
  seedPhraseGuard: boolean;
  // Optional: field key that should have a QR scan button
  qrScanField?: string;
}

export function DynamicForm({
  fields,
  title,
  initialData = {},
  onSubmit,
  onCancel,
  seedPhraseGuard,
  qrScanField,
}: DynamicFormProps) {
  const [data, setData] = useState<FormData>(initialData);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const handleChange = (key: string, value: string | number | boolean) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  const handleScan = (result: string) => {
    if (qrScanField) {
      handleChange(qrScanField, result);
    }
    setShowScanner(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    for (const field of fields) {
      if (field.required) {
        const val = data[field.key];
        if (val === undefined || val === '' || val === null) {
          setError(`${field.label} is required.`);
          return;
        }
      }
    }

    // Seed phrase guard
    if (seedPhraseGuard) {
      const stringData: Record<string, string> = {};
      for (const [key, val] of Object.entries(data)) {
        if (typeof val === 'string') stringData[key] = val;
      }
      const seedField = findSeedPhraseInForm(stringData);
      if (seedField) {
        setError(getSeedPhraseWarning());
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save. Is the vault unlocked?');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Form title */}
      <h2 className="text-lg font-semibold text-vault-100">{title}</h2>

      {/* Render fields dynamically */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((field) => {
          const colSpan = field.halfWidth ? 'sm:col-span-1' : 'sm:col-span-2';

          return (
            <div key={field.key} className={colSpan}>
              <label className="vault-label">
                {field.label}
                {field.required && <span className="text-red-400 ml-1">*</span>}
              </label>

              {field.type === 'textarea' ? (
                <textarea
                  value={String(data[field.key] || '')}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  className="vault-input min-h-[80px] resize-y"
                  placeholder={field.placeholder}
                />
              ) : field.type === 'select' ? (
                <select
                  value={String(data[field.key] || '')}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  className="vault-input"
                >
                  <option value="">Select...</option>
                  {field.options?.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : field.type === 'boolean' ? (
                <label className="flex items-center gap-3 py-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(data[field.key])}
                    onChange={(e) => handleChange(field.key, e.target.checked)}
                    className="w-5 h-5 rounded accent-accent-500"
                  />
                  <span className="text-sm text-vault-300">{field.label}</span>
                </label>
              ) : (
                <div className="flex gap-2">
                  <input
                    type={
                      field.type === 'encrypted' ? 'text' :
                      field.type === 'number' ? 'number' :
                      field.type === 'date' ? 'date' :
                      'text'
                    }
                    value={String(data[field.key] || '')}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    className="vault-input flex-1"
                    placeholder={field.placeholder}
                    autoComplete="off"
                    step={field.type === 'number' ? 'any' : undefined}
                  />
                  {/* QR scan button for encrypted fields */}
                  {field.type === 'encrypted' && qrScanField === field.key && (
                    <button
                      type="button"
                      onClick={() => setShowScanner(true)}
                      className="vault-button-secondary !px-3"
                      title="Scan QR code"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><line x1="14" y1="14" x2="14" y2="21" /><line x1="18" y1="14" x2="21" y2="14" /><line x1="21" y1="18" x2="21" y2="21" />
                      </svg>
                    </button>
                  )}
                </div>
              )}

              {field.type === 'encrypted' && (
                <p className="text-xs text-vault-600 mt-1">
                  Encrypted with AES-256-GCM before storage.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {error && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={isSubmitting} className="vault-button-primary flex-1">
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="vault-button-secondary">
          Cancel
        </button>
      </div>

      {/* QR Scanner overlay */}
      {showScanner && (
        <QRScanner onScan={handleScan} onClose={() => setShowScanner(false)} />
      )}
    </form>
  );
}
